import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import apiFetch from "../utils/apiFetch";
import UserContext from "../hooks/UserContext";
import SmModal, { ConfirmModal } from "../components/substation/SmModal";
import FieldForm from "../components/substation/FieldForm";
import AuditLogModal from "../components/substation/AuditLogModal";

const BASE_NET_FIELDS = [
  { key: "name", label: "Номи", type: "text", required: true },
  { key: "voltage", label: "Кучланиш (кВ)", type: "text" },
  { key: "length", label: "Узунлик (км)", type: "text" },
  { key: "span", label: "Оралиқ", type: "text" },
  { key: "start_year", label: "Ишга тушган йили", type: "text" },
  { key: "wire_type", label: "Сим тури", type: "text" },
  { key: "wire_protection_type", label: "Ҳимоя сими тури", type: "text" },
  { key: "optical_wire_type", label: "Оптик сим тури", type: "text" },
  { key: "poles_quantity", label: "Опоралар сони", type: "text" },
  { key: "intersections", label: "Кесишмалар", type: "text" },
];

const NETWORK_CURRENT_FIELDS = [
  { key: "conductor_allowed_nominal_current_a", label: "Ўтказгичнинг рухсат этилган номинал токи (A)", type: "number", min: "0", step: "any", inputMode: "decimal" },
  { key: "network_max_load_current_a", label: "Тармоқнинг максимал юклама токи, факт бўйича (A)", type: "number", min: "0", step: "any", inputMode: "decimal" },
];

const NET_FIELD_GROUPS = [
  { title: null, fields: BASE_NET_FIELDS },
  { title: "Ўтказгич ток кўрсаткичлари", fields: NETWORK_CURRENT_FIELDS },
];

const NET_FIELDS = NET_FIELD_GROUPS.flatMap((group) =>
  group.fields.map((field) => (group.title ? { ...field, group: group.title } : field))
);

const POLE_FIELDS = [
  { key: "name", label: "Номи", type: "text", required: true },
  { key: "pole_type", label: "Тури", type: "text" },
  { key: "pole_brand", label: "Маркаси", type: "text" },
  { key: "latitude", label: "Кенглик", type: "text" },
  { key: "longitude", label: "Узунлик", type: "text" },
  { key: "wire_type", label: "Сим тури", type: "text" },
  { key: "lightning_wire_type", label: "Чақмоқ сими", type: "text" },
  { key: "optical_wire_type", label: "Оптик сим", type: "text" },
  { key: "insulator_type", label: "Изолятор тури", type: "text" },
  { key: "fittings", label: "Арматура", type: "text" },
  { key: "bird_protection", label: "Қуш ҳимояси", type: "text" },
  { key: "order_index", label: "Тартиб рақами", type: "text" },
];

const INSP_FIELDS = [
  { key: "topic", label: "Мавзу", type: "text", required: true },
  { key: "date", label: "Сана", type: "date", required: true },
  { key: "section", label: "Участка", type: "text" },
  { key: "comment", label: "Изоҳ", type: "textarea" },
];

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return value;
}

export default function NetworkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { permissions, user } = useContext(UserContext);
  const isGlobal = user?.is_global_access;

  const canCreate = permissions.includes("NETWORK_CREATE");
  const canEdit = permissions.includes("NETWORK_EDIT");
  const canDelete = permissions.includes("NETWORK_DELETE");

  const [net, setNet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("info");
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [cities, setCities] = useState([]);

  const [poles, setPoles] = useState([]);
  const [polesLoading, setPolesLoading] = useState(false);
  const [inspections, setInspections] = useState([]);
  const [inspLoading, setInspLoading] = useState(false);

  const [poleModal, setPoleModal] = useState(null);
  const [inspModal, setInspModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedPole, setExpandedPole] = useState(null);

  const loadedRef = useRef({ poles: false, inspections: false });

  const buildForm = useCallback(
    (data) => {
      const out = {};
      NET_FIELDS.forEach((f) => {
        out[f.key] = data?.[f.key] ?? "";
      });
      if (isGlobal) out.cityId = data?.cityId ?? "";
      return out;
    },
    [isGlobal]
  );

  useEffect(() => {
    if (!isGlobal) return;
    apiFetch("/api/city")
      .then((r) => r.ok && r.json().then(setCities))
      .catch(() => {});
  }, [isGlobal]);

  const loadNet = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/network-management/${id}`);
      if (!res.ok) throw new Error("Тармоқ топилмади");
      const data = await res.json();
      setNet(data);
      setForm(buildForm(data));
    } catch (e) {
      console.error(e);
      setNet(null);
    } finally {
      setLoading(false);
    }
  }, [buildForm, id]);

  const loadPoles = useCallback(
    async (force = false) => {
      if (!force && loadedRef.current.poles) return;
      setPolesLoading(true);
      try {
        const res = await apiFetch(`/api/network-management/${id}/poles`);
        if (res.ok) {
          setPoles(await res.json());
          loadedRef.current.poles = true;
        }
      } catch (e) {
        console.error(e);
      } finally {
        setPolesLoading(false);
      }
    },
    [id]
  );

  const loadInspections = useCallback(
    async (force = false) => {
      if (!force && loadedRef.current.inspections) return;
      setInspLoading(true);
      try {
        const res = await apiFetch(`/api/network-management/${id}/inspections`);
        if (res.ok) {
          setInspections(await res.json());
          loadedRef.current.inspections = true;
        }
      } catch (e) {
        console.error(e);
      } finally {
        setInspLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    setPoles([]);
    setInspections([]);
    loadedRef.current = { poles: false, inspections: false };
    setActiveTab("info");
    setEditMode(false);
    setLoading(true);
    loadNet();
  }, [loadNet]);

  useEffect(() => {
    if (activeTab === "poles") loadPoles();
    if (activeTab === "inspections") loadInspections();
  }, [activeTab, loadInspections, loadPoles]);

  function flash(msg, type = "info") {
    setNotice({ msg, type });
    setTimeout(() => setNotice(null), 3500);
  }

  async function handleSaveInfo() {
    if (!form.name?.trim()) return flash("Номи киритилиши шарт", "error");
    setSaving(true);
    try {
      const res = await apiFetch(`/api/network-management/${id}`, { method: "PATCH", body: form });
      if (!res.ok) throw new Error((await res.json()).message);
      flash("Сақланди", "success");
      setEditMode(false);
      loadNet();
    } catch (e) {
      flash(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteNet() {
    try {
      const res = await apiFetch(`/api/network-management/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).message);
      navigate("/networks");
    } catch (e) {
      flash(e.message, "error");
    }
    setShowDeleteConfirm(false);
  }

  async function handleSavePole() {
    const data = poleModal.data;
    if (!data.name?.trim()) return flash("Номи киритилиши шарт", "error");
    try {
      const url =
        poleModal.mode === "create"
          ? `/api/network-management/${id}/poles`
          : `/api/network-management/${id}/poles/${poleModal.item.id}`;
      const res = await apiFetch(url, { method: poleModal.mode === "create" ? "POST" : "PATCH", body: data });
      if (!res.ok) throw new Error((await res.json()).message);
      setPoleModal(null);
      loadPoles(true);
      loadNet();
    } catch (e) {
      flash(e.message, "error");
    }
  }

  async function handleSaveInsp() {
    const data = inspModal.data;
    if (!data.topic?.trim()) return flash("Мавзу киритилиши шарт", "error");
    try {
      const url =
        inspModal.mode === "create"
          ? `/api/network-management/${id}/inspections`
          : `/api/network-management/${id}/inspections/${inspModal.item.id}`;
      const res = await apiFetch(url, { method: inspModal.mode === "create" ? "POST" : "PATCH", body: data });
      if (!res.ok) throw new Error((await res.json()).message);
      setInspModal(null);
      loadInspections(true);
      loadNet();
    } catch (e) {
      flash(e.message, "error");
    }
  }

  async function handleDeleteItem() {
    const { type, item } = deleteTarget;
    try {
      const url =
        type === "pole"
          ? `/api/network-management/${id}/poles/${item.id}`
          : `/api/network-management/${id}/inspections/${item.id}`;
      const res = await apiFetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).message);
      setDeleteTarget(null);
      if (type === "pole") {
        loadPoles(true);
        loadNet();
      } else {
        loadInspections(true);
        loadNet();
      }
    } catch (e) {
      flash(e.message, "error");
    }
  }

  function fmtDate(d) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("uz-UZ");
  }

  function openEditPole(p) {
    const vals = {};
    POLE_FIELDS.forEach((f) => {
      vals[f.key] = p[f.key] ?? "";
    });
    setPoleModal({ mode: "edit", data: vals, item: p });
  }

  const metrics = [
    { label: "Кучланиш", value: net?.voltage ? `${net.voltage} кВ` : "—" },
    { label: "Узунлик", value: net?.length ? `${net.length} км` : "—" },
    { label: "Опоралар", value: net?.polesCount ?? poles.length ?? 0 },
    { label: "Эксплуатация", value: net?.inspectionsCount ?? inspections.length ?? 0 },
    { label: "Филиал", value: net?.city?.name || "—" },
    { label: "Ишга тушган йили", value: net?.start_year || "—" },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="sm-page sm-page--detail sm-page--network-detail">
          <div className="sm-detail-state">
            <div className="sm-spinner" />
            <span>Юкланмоқда...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!net) {
    return (
      <Layout>
        <div className="sm-page sm-page--detail sm-page--network-detail">
          <div className="sm-detail-state sm-detail-state--error">
            <strong>Тармоқ топилмади</strong>
            <Link to="/networks" className="sm-btn sm-btn--ghost">
              &larr; Рўйхатга қайтиш
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="sm-page sm-page--detail sm-page--network-detail">
        <Link to="/networks" className="sm-detail-back">
          &larr; Рўйхатга қайтиш
        </Link>

        {notice && <div className={`sm-notice sm-notice--${notice.type}`}>{notice.msg}</div>}

        <section className="sm-detail-hero">
          <div className="sm-detail-hero__main">
            <p className="sm-detail-hero__eyebrow">Тармоқ бошқарув панели</p>
            <h1 className="sm-detail-hero__title">{displayValue(net.name)}</h1>
            <div className="sm-detail-hero__badges">
              {net.voltage && <span className="sm-badge sm-badge--voltage">{net.voltage} кВ</span>}
              {net.length && <span className="sm-badge sm-badge--branch">{net.length} км</span>}
              {net.city && <span className="sm-badge sm-badge--branch">{net.city.name}</span>}
            </div>
          </div>
          <div className="sm-detail-hero__actions">
            <button className="sm-btn sm-btn--ghost" onClick={() => setShowAudit(true)}>
              Тарих
            </button>
            {canDelete && (
              <button className="sm-btn sm-btn--danger" onClick={() => setShowDeleteConfirm(true)}>
                Ўчириш
              </button>
            )}
          </div>
        </section>

        <section className="sm-detail-metrics" aria-label="Network summary">
          {metrics.map((metric) => (
            <div className="sm-detail-metric" key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
            </div>
          ))}
        </section>

        <div className="sm-detail-layout">
          <aside className="sm-detail-nav" aria-label="Network sections">
            <button className={`sm-detail-nav__item ${activeTab === "info" ? "is-active" : ""}`} onClick={() => setActiveTab("info")}>
              <span>Умумий маълумот</span>
            </button>
            <button className={`sm-detail-nav__item ${activeTab === "poles" ? "is-active" : ""}`} onClick={() => setActiveTab("poles")}>
              <span>Опоралар</span>
              {net.polesCount > 0 && <b>{net.polesCount}</b>}
            </button>
            <button
              className={`sm-detail-nav__item ${activeTab === "inspections" ? "is-active" : ""}`}
              onClick={() => setActiveTab("inspections")}
            >
              <span>Эксплуатация ва таъмир</span>
              {net.inspectionsCount > 0 && <b>{net.inspectionsCount}</b>}
            </button>
          </aside>

          <main className="sm-detail-workspace">
            {activeTab === "info" && (
              <section className="sm-workspace-card">
                <div className="sm-workspace-card__head">
                  <div>
                    <p>Линия паспорти</p>
                    <h2>Умумий маълумот</h2>
                  </div>
                  {canEdit && !editMode && (
                    <button className="sm-btn sm-btn--primary" onClick={() => setEditMode(true)}>
                      Таҳрирлаш
                    </button>
                  )}
                </div>

                {editMode ? (
                  <>
                    <FieldForm fields={NET_FIELDS} values={form} onChange={setForm} className="sm-form sm-form--detail" />
                    {isGlobal && (
                      <div className="sm-form sm-form--detail sm-form--city">
                        <div className="sm-field">
                          <label className="sm-field__label">Филиал</label>
                          <select
                            className="sm-field__select"
                            value={form.cityId || ""}
                            onChange={(e) => setForm((p) => ({ ...p, cityId: e.target.value ? Number(e.target.value) : "" }))}
                          >
                            <option value="">Танланг...</option>
                            {cities.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    <div className="sm-detail-actionbar">
                      <button className="sm-btn sm-btn--ghost" onClick={() => { setEditMode(false); setForm(buildForm(net)); }}>
                        Бекор
                      </button>
                      <button className="sm-btn sm-btn--primary" onClick={handleSaveInfo} disabled={saving}>
                        {saving ? "Сақланмоқда..." : "Сақлаш"}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="sm-detail-field-grid">
                    {NET_FIELD_GROUPS.map((group) => (
                      <React.Fragment key={group.title || "base"}>
                        {group.title && <div className="sm-detail-field-group-title">{group.title}</div>}
                        {group.fields.map((f) => (
                          <div key={f.key} className="sm-detail-field">
                            <span>{f.label}</span>
                            <strong>{displayValue(net[f.key])}</strong>
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                    <div className="sm-detail-field">
                      <span>Филиал</span>
                      <strong>{displayValue(net.city?.name)}</strong>
                    </div>
                  </div>
                )}
              </section>
            )}

            {activeTab === "poles" && (
              <section className="sm-workspace-card">
                <div className="sm-workspace-card__head">
                  <div>
                    <p>Боғланган объектлар</p>
                    <h2>Опоралар <span>{poles.length}</span></h2>
                  </div>
                  {canCreate && (
                    <button className="sm-btn sm-btn--primary" onClick={() => setPoleModal({ mode: "create", data: {} })}>
                      Қўшиш
                    </button>
                  )}
                </div>

                {polesLoading ? (
                  <div className="sm-detail-state">
                    <div className="sm-spinner" />
                    <span>Юкланмоқда...</span>
                  </div>
                ) : poles.length === 0 ? (
                  <div className="sm-detail-empty">Ҳали опоралар қўшилмаган</div>
                ) : (
                  <div className="sm-asset-list">
                    {poles.map((p) => (
                      <article key={p.id} className="sm-asset-row">
                        <button className="sm-asset-row__head" onClick={() => setExpandedPole(expandedPole === p.id ? null : p.id)}>
                          <span className="sm-asset-row__title">{p.name || `#${p.id}`}</span>
                          <span className="sm-asset-row__meta">{p.pole_type || "—"} · {p.pole_brand || "—"}</span>
                          <span className={`sm-asset-row__toggle ${expandedPole === p.id ? "is-open" : ""}`}>&#9660;</span>
                        </button>

                        {expandedPole === p.id && (
                          <div className="sm-asset-row__body">
                            <div className="sm-detail-field-grid">
                              {POLE_FIELDS.map((f) => (
                                <div key={f.key} className="sm-detail-field">
                                  <span>{f.label}</span>
                                  <strong>{displayValue(p[f.key])}</strong>
                                </div>
                              ))}
                            </div>
                            <div className="sm-detail-actionbar">
                              {canEdit && (
                                <button className="sm-btn sm-btn--ghost" onClick={() => openEditPole(p)}>
                                  Таҳрирлаш
                                </button>
                              )}
                              {canDelete && (
                                <button className="sm-btn sm-btn--danger" onClick={() => setDeleteTarget({ type: "pole", item: p })}>
                                  Ўчириш
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                )}
              </section>
            )}

            {activeTab === "inspections" && (
              <section className="sm-workspace-card">
                <div className="sm-workspace-card__head">
                  <div>
                    <p>Эксплуатация тарихи</p>
                    <h2>Эксплуатация ва таъмир <span>{inspections.length}</span></h2>
                  </div>
                  {canCreate && (
                    <button className="sm-btn sm-btn--primary" onClick={() => setInspModal({ mode: "create", data: {} })}>
                      Қўшиш
                    </button>
                  )}
                </div>

                {inspLoading ? (
                  <div className="sm-detail-state">
                    <div className="sm-spinner" />
                    <span>Юкланмоқда...</span>
                  </div>
                ) : inspections.length === 0 ? (
                  <div className="sm-detail-empty">Ёзувлар топилмади</div>
                ) : (
                  <div className="sm-detail-table-wrap">
                    <table className="sm-maint-table sm-maint-table--detail">
                      <thead>
                        <tr>
                          <th>Сана</th>
                          <th>Мавзу</th>
                          <th>Участка</th>
                          <th>Муаллиф</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {inspections.map((r) => (
                          <tr key={r.id}>
                            <td>{fmtDate(r.date)}</td>
                            <td>{displayValue(r.topic)}</td>
                            <td>{displayValue(r.section)}</td>
                            <td>{r.inspector_user ? `${r.inspector_user.name || ""} ${r.inspector_user.lastname || ""}`.trim() : "—"}</td>
                            <td>
                              <div className="sm-maint-actions">
                                {canEdit && (
                                  <button
                                    className="sm-btn sm-btn--ghost sm-btn--icon"
                                    onClick={() => {
                                      setInspModal({
                                        mode: "edit",
                                        data: {
                                          topic: r.topic || "",
                                          date: r.date ? r.date.split("T")[0] : "",
                                          section: r.section || "",
                                          comment: r.comment || "",
                                        },
                                        item: r,
                                      });
                                    }}
                                  >
                                    &#9998;
                                  </button>
                                )}
                                {canDelete && (
                                  <button className="sm-btn sm-btn--danger sm-btn--icon" onClick={() => setDeleteTarget({ type: "inspection", item: r })}>
                                    &#10005;
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </main>
        </div>

        {poleModal && (
          <SmModal
            title={poleModal.mode === "create" ? "Янги опора" : "Опорани таҳрирлаш"}
            onClose={() => setPoleModal(null)}
            wide
            footer={
              <>
                <button className="sm-btn sm-btn--ghost" onClick={() => setPoleModal(null)}>Бекор</button>
                <button className="sm-btn sm-btn--primary" onClick={handleSavePole}>Сақлаш</button>
              </>
            }
          >
            <FieldForm fields={POLE_FIELDS} values={poleModal.data} onChange={(d) => setPoleModal((p) => ({ ...p, data: d }))} className="sm-form sm-form--detail" />
          </SmModal>
        )}

        {inspModal && (
          <SmModal
            title={inspModal.mode === "create" ? "Янги ёзув" : "Ёзувни таҳрирлаш"}
            onClose={() => setInspModal(null)}
            footer={
              <>
                <button className="sm-btn sm-btn--ghost" onClick={() => setInspModal(null)}>Бекор</button>
                <button className="sm-btn sm-btn--primary" onClick={handleSaveInsp}>Сақлаш</button>
              </>
            }
          >
            <FieldForm fields={INSP_FIELDS} values={inspModal.data} onChange={(d) => setInspModal((p) => ({ ...p, data: d }))} className="sm-form sm-form--detail" />
          </SmModal>
        )}

        {deleteTarget && (
          <ConfirmModal
            title="Ўчириш"
            text={`"${deleteTarget.item.name || deleteTarget.item.topic || `#${deleteTarget.item.id}`}" ўчирилсинми?`}
            subText="Маълумот аудит-логга сақланади."
            onConfirm={handleDeleteItem}
            onCancel={() => setDeleteTarget(null)}
          />
        )}

        {showAudit && <AuditLogModal apiUrl={`/api/network-management/${id}/audit-log`} onClose={() => setShowAudit(false)} />}
        {showDeleteConfirm && (
          <ConfirmModal
            title="Тармоқни ўчириш"
            text={`"${net.name}" ўчирилсинми?`}
            subText="Барча опоралар ва ёзувлар ҳам ўчирилади. Маълумот аудит-логга сақланади."
            onConfirm={handleDeleteNet}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        )}
      </div>
    </Layout>
  );
}
